import { computed, h, defineComponent, ref, SetupContext, watch } from 'vue';
// @ts-ignore
import { BTaginput } from 'buefy/dist/esm/taginput';
import { t } from '@/ui/i18n/i18n';
import { SelectOption, SelectOptions } from '@/ui/types';
import { cloneDeep } from 'lodash';

export interface GroupOptions<I extends SelectOption> {
  type: string;
  items: I[];
}

export interface StackableTagInputProps<T = number> {
  value: T[];
  options: SelectOptions<T> | GroupOptions<SelectOption>[];
  disabled: boolean;
  icon?: string;
  placeholder?: string;
  groupField?: string;
  groupOptions?: string;
  name?: string;
  nonVisibleOptions?: SelectOptions<T>;
  maxListedOptions?: number;
}

export const StackableTagInput = defineComponent<StackableTagInputProps>({
  name: 'StackableTagInput',
  props: ['value', 'icon', 'options', 'disabled', 'placeholder', 'groupField', 'groupOptions', 'name',
    'nonVisibleOptions', 'maxListedOptions'],
  setup(props, context: SetupContext) {
    const filterString = ref('');
    const selectedList = ref(props.value);

    const search = (option: SelectOption) => option.label
      .toLowerCase()
      .indexOf(filterString.value && filterString.value.trim().toLowerCase()) >= 0;

    const findTagValueOption = (value: number): SelectOption =>
      (([...props.options, ...(props.nonVisibleOptions ?? [])]) as SelectOptions)
        .find(option => option.value === value)!;

    const setSelectedList = (options: number[]): void => {
      selectedList.value = options;
      context.emit('input', options);
    };
    //maps selected ids|strings into representation
    const tagValues = computed(() => {
      return selectedList.value.map(value => {
        const valueOption: SelectOption = findTagValueOption(value);

        //if input was a custom value, not selected from options, make it an option format
        return valueOption !== undefined
          ? valueOption
          : {
            value,
            label: value.toString(),
          } as SelectOption;
      });
    });

    const filteredOptions = computed(() => {
      let filtered = cloneDeep(props.options);

      //filter by search typing
      filtered = (filtered as SelectOptions).filter(search).map((option: SelectOption) => {
        return {
          ...option,
          selected: selectedList.value.includes(option.value),
        };
      });

      return filtered.slice(0, props.maxListedOptions);
    });

    const showStackTag = computed(() => {
      const characterCount = tagValues.value.map(option => option.label).join('').length;
      return tagValues.value.length > 1 && characterCount > 10;
    });

    const disableChromeAutocomplete = {
      role: 'presentation',
      autocomplete: 'false',
    };

    // update selectedList when user reset the filter
    watch(() => props.value, value => {
      selectedList.value = value;
    });

    return function render() {
      return h(BTaginput, {
        props: {
          openOnFocus: true,
          data: filteredOptions.value,
          field: 'label',
          value: showStackTag.value
            ? [{ label: t('common.selected-items', { value: selectedList.value.length.toString() }) }]
            : tagValues.value,
          icon: props.icon,
          attached: true,
          autocomplete: true,
          ellipsis: true,
          clearOnSelect: true,
          groupField: props.groupField,
          groupOptions: props.groupOptions,
          beforeAdding: (tag: SelectOption) => {
            const isValid = !selectedList.value.includes(tag.value) && tag.value !== undefined;
            if (!isValid) {
              setSelectedList(selectedList.value.filter(v => v !== tag.value));
            } else {
              setSelectedList([...selectedList.value, tag.value]);
            }
            return isValid;
          },
        },
        scopedSlots: {
          ...context.slots,
          default: item =>
            h('div', { class: `dropdown-item-wrapper ${item.option.selected && 'is-active'}` },
              [h('span', item.option.label)]),
        },
        attrs: {
          ...disableChromeAutocomplete,
          disabled: props.disabled,
          placeholder: props.placeholder,
          name: props.name,
        },
        on: {
          typing: (value: string) => {
            filterString.value = value;
            context.emit('typing', value);
          },
          focus: (e: FocusEvent) => {
            context.emit('focus', e);
          },
          blur: (e: FocusEvent) => {
            context.emit('blur', e);
          },
          remove: (option: SelectOption) => {
            if (option.value !== undefined) {
              // remove tag
              setSelectedList(selectedList.value.filter(v => v !== option.value));
            } else {
              // remove stackable tag
              setSelectedList([]);
            }
          },
        },
      }, [
        h('template', { slot: 'empty' }, [
          context.slots.empty ? context.slots.empty()
            : h('div', { class: 'dropdown-item-wrapper' },
              [h('span', t('form.no-matches'))]),
        ]),
        context.slots.header ? h('template', { slot: 'header' }, [
          context.slots.header(),
        ]) : null,
      ]);
    };
  },
});
